import { createSlice } from '@reduxjs/toolkit';
import {
  fetchCompanyDetails,
  changeSellerPassword,
  fetchSellerCouriers,
  fetchSellerWebhooks,
  fetchAvailableCouriers,
  createSellerCourier,
  updateSellerCourier,
  deleteSellerCourier,
  updateWarehousePickupSchedule,
} from './sellerDetailsThunks';

const initialState = {
  company: null,
  loading: false,
  error: null,
  passwordChanging: false,
  /** The company's `SettingCourier` rows. */
  settingCouriers: [],
  /** The company's `SettingWebhook` rows, read-only here. */
  settingWebhooks: [],
  /** Couriers the admin can add: `[{ id, courier }]`. */
  availableCouriers: [],
  /** The last courier action that failed, as the legacy showed it. */
  courierError: null,
  courierSaving: false,
  /** The row being toggled or deleted, to disable only its controls. */
  courierBusyId: null,
  /** The warehouse whose pickup hours are being saved. */
  warehouseScheduleSavingId: null,
};

const sellerDetailsSlice = createSlice({
  name: 'sellerDetails',
  initialState,
  reducers: {
    clearCompanyDetails: (state) => {
      state.company = null;
      state.error = null;
      state.settingCouriers = [];
      state.settingWebhooks = [];
      state.availableCouriers = [];
      state.courierError = null;
    },
    clearCourierError: (state) => {
      state.courierError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Details
      .addCase(fetchCompanyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.company = action.payload;
      })
      .addCase(fetchCompanyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Change Password
      .addCase(changeSellerPassword.pending, (state) => {
        state.passwordChanging = true;
      })
      .addCase(changeSellerPassword.fulfilled, (state) => {
        state.passwordChanging = false;
      })
      .addCase(changeSellerPassword.rejected, (state) => {
        state.passwordChanging = false;
      })
      // Courier settings
      .addCase(fetchSellerCouriers.fulfilled, (state, action) => {
        state.settingCouriers = action.payload;
      })
      .addCase(fetchSellerWebhooks.fulfilled, (state, action) => {
        state.settingWebhooks = action.payload;
      })
      .addCase(fetchAvailableCouriers.fulfilled, (state, action) => {
        state.availableCouriers = action.payload;
      })
      .addCase(createSellerCourier.pending, (state) => {
        state.courierSaving = true;
        state.courierError = null;
      })
      .addCase(createSellerCourier.fulfilled, (state, action) => {
        state.courierSaving = false;
        state.settingCouriers = [...state.settingCouriers, action.payload];
      })
      .addCase(createSellerCourier.rejected, (state, action) => {
        state.courierSaving = false;
        state.courierError = action.payload;
      })
      .addCase(updateSellerCourier.pending, (state, action) => {
        state.courierBusyId = action.meta.arg.settingCourierId;
        state.courierError = null;
      })
      .addCase(updateSellerCourier.fulfilled, (state, action) => {
        state.courierBusyId = null;
        state.settingCouriers = state.settingCouriers.map((settingCourier) =>
          settingCourier.id === action.payload.id ? action.payload : settingCourier
        );
      })
      .addCase(updateSellerCourier.rejected, (state, action) => {
        state.courierBusyId = null;
        state.courierError = action.payload;
      })
      .addCase(deleteSellerCourier.pending, (state, action) => {
        state.courierBusyId = action.meta.arg.settingCourierId;
        state.courierError = null;
      })
      .addCase(deleteSellerCourier.fulfilled, (state, action) => {
        state.courierBusyId = null;
        state.settingCouriers = state.settingCouriers.filter(
          (settingCourier) => settingCourier.id !== action.payload
        );
      })
      .addCase(deleteSellerCourier.rejected, (state, action) => {
        state.courierBusyId = null;
        state.courierError = action.payload;
      })
      // Pickup hours of a warehouse. The failure is not stored: the modal that
      // asked for it is still open and shows it there, while `error` would
      // replace the whole page with a load failure.
      .addCase(updateWarehousePickupSchedule.pending, (state, action) => {
        state.warehouseScheduleSavingId = action.meta.arg.warehouseId;
      })
      .addCase(updateWarehousePickupSchedule.fulfilled, (state, action) => {
        state.warehouseScheduleSavingId = null;
        if (!state.company) return;
        state.company.warehouses = state.company.warehouses.map((warehouse) =>
          warehouse.id === action.payload.id ? action.payload : warehouse
        );
      })
      .addCase(updateWarehousePickupSchedule.rejected, (state) => {
        state.warehouseScheduleSavingId = null;
      });
  }
});

export const { clearCompanyDetails, clearCourierError } = sellerDetailsSlice.actions;

export default sellerDetailsSlice.reducer;
